/**
 * Analytics Dashboard
 *
 * Shows live evaluation analytics from the backend. Predictive student-risk
 * analytics are intentionally rendered as a roadmap placeholder: no predictive
 * model is deployed yet, and this page must never fabricate model output.
 */

import React, { useState, useEffect, useCallback } from 'react';

import {
  MetricCard,
  SimpleBarChart,
  RiskDistributionChart,
} from '../../components/analytics/AnalyticsCharts';
import {
  EvaluationStats,
  evaluationService,
} from '../../services/evaluationService';

const STATUS_LABELS: Record<string, string> = {
  draft: 'Draft',
  pending: 'Pending',
  in_progress: 'In Progress',
  scored: 'Scored',
  completed: 'Completed',
  approved: 'Approved',
  archived: 'Archived',
};

const PERFORMANCE_LEVEL_COLORS: Record<string, string> = {
  highly_effective: '#22c55e',
  effective: '#3b82f6',
  developing: '#f97316',
  ineffective: '#ef4444',
};

const PERFORMANCE_LEVEL_LABELS: Record<string, string> = {
  highly_effective: 'Highly Effective',
  effective: 'Effective',
  developing: 'Developing',
  ineffective: 'Ineffective',
};

const StudentSuccessAnalytics: React.FC = () => {
  const [stats, setStats] = useState<EvaluationStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const loadStats = useCallback(async () => {
    try {
      setError(null);
      const data = await evaluationService.getEvaluationStats();
      setStats(data);
      setLastUpdated(new Date());
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to load analytics data'
      );
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    void loadStats();
  }, [loadStats]);

  const handleRefresh = () => {
    setRefreshing(true);
    void loadStats();
  };

  const statusChartData = stats
    ? Object.entries(stats.status_distribution)
        .filter(([, count]) => count > 0)
        .map(([statusKey, count]) => ({
          name: STATUS_LABELS[statusKey] ?? statusKey,
          value: count,
        }))
    : [];

  const performanceChartData = stats
    ? Object.entries(stats.performance_level_distribution).map(
        ([level, count]) => ({
          name: PERFORMANCE_LEVEL_LABELS[level] ?? level,
          value: count,
          color: PERFORMANCE_LEVEL_COLORS[level] ?? '#6b7280',
        })
      )
    : [];

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div>
      {/* Toolbar (page title comes from the dashboard layout) */}
      <div className="flex justify-end items-center mb-6 space-x-3">
        {lastUpdated && (
          <span className="text-sm text-gray-500">
            Updated {lastUpdated.toLocaleTimeString()}
          </span>
        )}
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors"
        >
          {refreshing ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      {/* Error state */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-8">
          <p className="text-sm text-red-800">
            Could not load analytics: {error}
          </p>
          <button
            onClick={handleRefresh}
            className="mt-2 text-sm font-medium text-red-700 underline"
          >
            Try again
          </button>
        </div>
      )}

      {stats && (
        <>
          {/* Evaluation metrics (live data) */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <MetricCard
              title="Total Evaluations"
              value={stats.total_evaluations}
              color="primary"
              subtitle="All evaluations in your district"
            />
            <MetricCard
              title="Completed"
              value={stats.completed_evaluations}
              color="success"
              subtitle="Scored or completed"
            />
            <MetricCard
              title="Completion Rate"
              value={stats.completion_rate}
              format="percentage"
              color="secondary"
              subtitle="Of all evaluations"
            />
            <MetricCard
              title="Average Score"
              value={stats.score_statistics.average_score}
              format="percentage"
              color="warning"
              subtitle={`Across ${stats.score_statistics.count} scored evaluation${stats.score_statistics.count === 1 ? '' : 's'}`}
            />
          </div>

          {/* Distributions */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
            {statusChartData.length > 0 ? (
              <SimpleBarChart
                data={statusChartData}
                title="Evaluations by Status"
                color="#3b82f6"
              />
            ) : (
              <div className="bg-white rounded-lg shadow border p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Evaluations by Status
                </h3>
                <p className="text-sm text-gray-600">
                  No evaluations yet. Create an evaluation to see status
                  analytics here.
                </p>
              </div>
            )}
            {performanceChartData.length > 0 ? (
              <RiskDistributionChart
                data={performanceChartData}
                title="Performance Level Distribution"
              />
            ) : (
              <div className="bg-white rounded-lg shadow border p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Performance Level Distribution
                </h3>
                <p className="text-sm text-gray-600">
                  No scored evaluations yet. Score an evaluation against its
                  rubric to see performance levels here.
                </p>
              </div>
            )}
          </div>
        </>
      )}

      {/* Roadmap: predictive student-risk analytics (not yet available) */}
      <div className="bg-white rounded-lg shadow border border-dashed border-gray-300 p-6">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              Predictive Student Risk Analytics
            </h3>
            <p className="text-sm text-gray-600 mt-1 max-w-2xl">
              At-risk identification, risk trends, and intervention
              effectiveness will appear here once the predictive model is
              trained and deployed. This section shows no data today because
              no model is running — this dashboard only reports real numbers.
            </p>
          </div>
          <span className="shrink-0 inline-flex items-center rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-600">
            Coming soon
          </span>
        </div>
      </div>
    </div>
  );
};

export default StudentSuccessAnalytics;
